import React, { useState } from 'react';
import { X, Printer, ShieldCheck, HeartPulse, CheckCircle2, Download, AlertOctagon, PhoneCall, Utensils, RotateCw, Bed } from 'lucide-react';
import { ClinicalDocument, Patient, Resident, ClinicalBusiness } from '../types';

interface PrintPreviewModalProps {
  document: ClinicalDocument;
  patient: Patient;
  isOpen: boolean;
  onClose: () => void;
  pocketResident?: Resident;
  initialMode?: 'clinicalEHR' | 'pocketCard';
  currentBusiness?: ClinicalBusiness;
}

export const PrintPreviewModal: React.FC<PrintPreviewModalProps> = ({
  document,
  patient,
  isOpen,
  onClose,
  pocketResident,
  initialMode = 'clinicalEHR',
  currentBusiness
}) => {
  const [printMode, setPrintMode] = useState<'clinicalEHR' | 'pocketCard'>(initialMode);

  React.useEffect(() => {
    if (isOpen) {
      setPrintMode(initialMode);
    }
  }, [initialMode, isOpen]);

  if (!isOpen) return null;

  const handleTriggerPrint = () => {
    window.print();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-4xl max-h-[92vh] flex flex-col shadow-2xl overflow-hidden">
        {/* Modal Controls Bar */}
        <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between bg-slate-950/80">
          <div className="flex items-center gap-3">
            <Printer className="h-5 w-5 text-teal-400" />
            <div>
              <h3 className="font-bold text-slate-100 text-sm">Official MediCore Print & Hardcopy Engine</h3>
              <p className="text-[10px] text-slate-400">
                {printMode === 'clinicalEHR'
                  ? 'Certified HL7 Inpatient Medical Record'
                  : 'Pocket Caregiver Cheat-Sheet & Shift Lanyard Badge'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {pocketResident && (
              <div className="flex items-center bg-slate-900 p-1 rounded-xl border border-slate-800 text-xs">
                <button
                  onClick={() => setPrintMode('clinicalEHR')}
                  className={`px-3 py-1 rounded-lg font-medium transition ${
                    printMode === 'clinicalEHR'
                      ? 'bg-teal-500 text-slate-950 font-bold'
                      : 'text-slate-400 hover:text-white'
                  }`}
                >
                  Clinical Note
                </button>
                <button
                  onClick={() => setPrintMode('pocketCard')}
                  className={`px-3 py-1 rounded-lg font-medium transition ${
                    printMode === 'pocketCard'
                      ? 'bg-indigo-500 text-white font-bold'
                      : 'text-slate-400 hover:text-white'
                  }`}
                >
                  Pocket Caregiver Card
                </button>
              </div>
            )}

            <button
              onClick={handleTriggerPrint}
              className="flex items-center gap-1.5 bg-teal-600 hover:bg-teal-500 text-slate-950 font-bold px-4 py-2 rounded-xl transition text-xs shadow-md shadow-teal-500/20"
            >
              <Printer className="h-4 w-4" />
              <span>Print Hardcopy</span>
            </button>
            <button
              onClick={onClose}
              className="p-2 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800 transition"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Scrollable Printable A4 / Card Sheet */}
        <div className="p-6 overflow-y-auto bg-slate-950 flex justify-center">
          {printMode === 'clinicalEHR' ? (
            /* Standard Hospital Letterhead Document */
            <div
              id="printable-document"
              className="w-full max-w-[800px] bg-white text-slate-900 p-10 rounded-sm shadow-xl font-sans text-xs border border-slate-200"
            >
              {/* Letterhead */}
              <div className="border-b-2 border-slate-900 pb-4 flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <div className="h-8 w-8 bg-slate-900 text-white rounded flex items-center justify-center font-bold">
                      MC
                    </div>
                    <div>
                      <h1 className="text-xl font-extrabold tracking-tight text-slate-900 leading-none">
                        MEDICORE MEMORIAL HOSPITAL
                      </h1>
                      <p className="text-[10px] text-slate-600 font-medium mt-0.5">
                        DEPARTMENT OF ADVANCED INPATIENT MEDICINE • TIER 1 CLINICAL CENTER
                      </p>
                    </div>
                  </div>
                  <p className="text-[9px] text-slate-500 mt-2">
                    4200 Medical Center Parkway, Suite 800 • Boston, MA 02115 • Tel: (617) 555-0199
                  </p>
                </div>

                <div className="text-right">
                  <span className="inline-block border border-slate-900 px-2 py-0.5 font-mono font-bold text-[10px] tracking-wider uppercase">
                    CONFIDENTIAL EHR RECORD
                  </span>
                  <p className="font-mono text-[9px] text-slate-500 mt-1">DOC ID: {document.id}</p>
                  <p className="font-mono text-[9px] text-slate-500">Date: {document.date}</p>
                </div>
              </div>

              {/* Patient Header Block */}
              <div className="mt-4 p-3 bg-slate-100 rounded border border-slate-300 grid grid-cols-4 gap-2 text-[11px]">
                <div>
                  <span className="text-[9px] uppercase font-bold text-slate-500 block">Patient Name</span>
                  <span className="font-bold text-slate-900 text-xs">{document.patientName}</span>
                </div>
                <div>
                  <span className="text-[9px] uppercase font-bold text-slate-500 block">MRN</span>
                  <span className="font-mono font-bold text-slate-900">{document.mrn}</span>
                </div>
                <div>
                  <span className="text-[9px] uppercase font-bold text-slate-500 block">DOB / Age / Gender</span>
                  <span>{patient.age} Yrs • {patient.gender}</span>
                </div>
                <div>
                  <span className="text-[9px] uppercase font-bold text-slate-500 block">Department / Bed</span>
                  <span>{patient.department} / {patient.bedNumber}</span>
                </div>
              </div>

              {/* Allergies & Vitals Bar */}
              <div className="mt-3 p-2 bg-slate-50 border border-slate-200 rounded flex items-center justify-between text-[10px]">
                <div>
                  <strong className="text-red-700">KNOWN ALLERGIES: </strong>
                  <span>{patient.allergies.join(', ') || 'No Known Drug Allergies'}</span>
                </div>
                <div className="font-mono space-x-3 text-slate-700">
                  <span>HR: {patient.vitals.heartRate} bpm</span>
                  <span>BP: {patient.vitals.bpSystolic}/{patient.vitals.bpDiastolic}</span>
                  <span>SpO2: {patient.vitals.spO2}%</span>
                  <span>Temp: {patient.vitals.tempC}°C</span>
                </div>
              </div>

              {/* Document Title */}
              <div className="mt-6 mb-3">
                <h2 className="text-base font-bold text-slate-900 border-b border-slate-300 pb-1 uppercase tracking-wide">
                  {document.docType}
                </h2>
              </div>

              {/* Chief Complaint */}
              <div className="mb-4">
                <h3 className="font-bold text-slate-800 text-[11px] uppercase tracking-wider mb-0.5">CHIEF COMPLAINT:</h3>
                <p className="text-slate-800 pl-2 border-l-2 border-slate-400 font-medium">{document.chiefComplaint}</p>
              </div>

              {/* SOAP Sections */}
              <div className="space-y-4">
                <div>
                  <h3 className="font-bold text-slate-800 text-[11px] uppercase tracking-wider mb-1">
                    1. SUBJECTIVE:
                  </h3>
                  <p className="text-slate-700 leading-relaxed whitespace-pre-line text-[11px] pl-2 border-l border-slate-300">
                    {document.subjective}
                  </p>
                </div>

                <div>
                  <h3 className="font-bold text-slate-800 text-[11px] uppercase tracking-wider mb-1">
                    2. OBJECTIVE:
                  </h3>
                  <p className="text-slate-700 leading-relaxed whitespace-pre-line text-[11px] pl-2 border-l border-slate-300">
                    {document.objective}
                  </p>
                </div>

                <div>
                  <h3 className="font-bold text-slate-800 text-[11px] uppercase tracking-wider mb-1">
                    3. ASSESSMENT:
                  </h3>
                  <p className="text-slate-700 leading-relaxed whitespace-pre-line text-[11px] pl-2 border-l border-slate-300">
                    {document.assessment}
                  </p>
                </div>

                <div>
                  <h3 className="font-bold text-slate-800 text-[11px] uppercase tracking-wider mb-1">
                    4. PLAN:
                  </h3>
                  <p className="text-slate-700 leading-relaxed whitespace-pre-line text-[11px] pl-2 border-l border-slate-300">
                    {document.plan}
                  </p>
                </div>
              </div>

              {/* Prescriptions */}
              {document.prescriptions.length > 0 && (
                <div className="mt-5 pt-3 border-t border-slate-300">
                  <h3 className="font-bold text-slate-800 text-[11px] uppercase tracking-wider mb-2">
                    ORDERED PHARMACOTHERAPY:
                  </h3>
                  <table className="w-full text-left text-[10px] border border-slate-300">
                    <thead className="bg-slate-100 border-b border-slate-300 font-bold text-slate-700">
                      <tr>
                        <th className="p-1.5">Medication</th>
                        <th className="p-1.5">Dosage</th>
                        <th className="p-1.5">Frequency</th>
                        <th className="p-1.5">Route</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {document.prescriptions.map((rx, idx) => (
                        <tr key={idx}>
                          <td className="p-1.5 font-bold text-slate-900">{rx.drug}</td>
                          <td className="p-1.5 font-mono">{rx.dosage}</td>
                          <td className="p-1.5">{rx.frequency}</td>
                          <td className="p-1.5">{rx.route}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* ICD-10 Coding */}
              {document.icd10Codes.length > 0 && (
                <div className="mt-4 pt-2">
                  <h3 className="font-bold text-slate-800 text-[10px] uppercase tracking-wider mb-1">
                    RECORDED ICD-10 CODES:
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {document.icd10Codes.map((code, idx) => (
                      <span key={idx} className="bg-slate-100 border border-slate-300 px-2 py-0.5 rounded text-[10px] text-slate-800">
                        <strong>{code.code}</strong> — {code.description}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Official Signature and Watermark */}
              <div className="mt-10 pt-6 border-t-2 border-slate-900 flex items-end justify-between">
                <div>
                  <p className="font-bold text-slate-900 text-xs">{document.author}</p>
                  <p className="text-[10px] text-slate-600">Attending Physician • License #MD-884920</p>
                  <div className="mt-2 flex items-center gap-2 text-emerald-800 font-mono text-[9px]">
                    <ShieldCheck className="h-3.5 w-3.5 text-emerald-700" />
                    <span>Digitally Validated with MediCore AI Unified Ledger</span>
                  </div>
                </div>

                <div className="text-right">
                  {currentBusiness?.featuresUnlocked?.digitalShaSignature ? (
                    <div className="border-2 border-emerald-700 bg-emerald-50 px-3 py-1.5 rounded text-right shadow-sm">
                      <span className="text-[9px] font-mono font-black text-emerald-800 uppercase block tracking-wider">
                        ★ OFFICIAL HOSPITAL SEAL
                      </span>
                      <span className="text-[8px] font-mono text-emerald-700 block font-bold">
                        CERTIFIED LEGAL EHR RECORD
                      </span>
                      <p className="font-mono text-[8px] text-emerald-950 font-bold mt-0.5">
                        {document.verificationHash || 'SHA256:7f83b1657ff1fc53b92dc18148a1d65dfc2d4b1fa3d677284addd200126d9069'}
                      </p>
                      <span className="text-[7px] font-mono text-slate-500 block">
                        Enterprise Key: {currentBusiness.licenseKey}
                      </span>
                    </div>
                  ) : (
                    <div className="border-2 border-dashed border-amber-600 bg-amber-50 px-3 py-1.5 rounded text-right">
                      <span className="text-[9px] font-mono font-black text-amber-800 uppercase block tracking-wider">
                        ⚠ PREVIEW DRAFT • UNOFFICIAL
                      </span>
                      <span className="text-[8px] font-mono text-amber-700 block">
                        Digital SHA-256 cryptographic seal requires Enterprise Unified license
                      </span>
                      <p className="font-mono text-[8px] text-slate-500 mt-0.5">
                        [UNSEALED TRIAL DRAFT]
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            /* Compact Pocket Caregiver Card / Lanyard Cheat Sheet */
            pocketResident && (
              <div
                id="printable-document"
                className="w-full max-w-[500px] bg-white text-slate-900 p-6 rounded-lg shadow-xl font-sans text-xs border-2 border-slate-900"
              >
                {/* Badge Header with Cut Line Notice */}
                <div className="border-b-2 border-slate-900 pb-3 flex items-center justify-between">
                  <div>
                    <span className="text-[9px] font-mono font-bold bg-slate-900 text-white px-2 py-0.5 rounded uppercase tracking-wider">
                      MEDICORE SENIOR LIVING • POCKET CAREGIVER CARD
                    </span>
                    <h2 className="text-xl font-black text-slate-900 mt-1">
                      {pocketResident.fullName}{' '}
                      <span className="text-teal-700 font-semibold text-base">("{pocketResident.preferredName}")</span>
                    </h2>
                    <p className="text-[11px] font-mono text-slate-600 font-bold">
                      ROOM: {pocketResident.roomNumber} • AGE: {pocketResident.age}
                    </p>
                  </div>
                  <div className="text-right">
                    <span className="border-2 border-red-700 text-red-700 px-2 py-1 font-mono font-black text-xs block">
                      {pocketResident.codeStatus}
                    </span>
                  </div>
                </div>

                {/* Primary Safety Alerts */}
                <div className="mt-3 grid grid-cols-2 gap-2 text-[11px]">
                  <div className="p-2.5 bg-red-50 border border-red-300 rounded">
                    <span className="font-black text-red-800 block text-[10px] uppercase">FALL RISK STATUS</span>
                    <span className="font-bold text-red-900 text-xs">{pocketResident.fallRisk}</span>
                    <p className="text-[9px] text-red-700 mt-0.5">Morse Score: {pocketResident.fallRiskScoreMorse} / 125</p>
                  </div>

                  <div className="p-2.5 bg-blue-50 border border-blue-300 rounded">
                    <span className="font-black text-blue-800 block text-[10px] uppercase">TRANSFER PROTOCOL</span>
                    <span className="font-bold text-blue-900 text-xs">{pocketResident.mobilityTransfer}</span>
                    <p className="text-[9px] text-blue-700 mt-0.5">Requires 2 Staff Verification</p>
                  </div>
                </div>

                {/* Dietary Consistency Cheat-Sheet */}
                <div className="mt-3 p-3 bg-amber-50 border border-amber-300 rounded text-[11px]">
                  <span className="font-black text-amber-900 block text-[10px] uppercase">
                    DIETARY & FLUID CONSISTENCY (ASPIRATION PRECAUTION)
                  </span>
                  <div className="grid grid-cols-2 gap-2 mt-1 font-bold text-slate-900">
                    <div>
                      <span className="text-slate-600 text-[10px] block font-normal">Food Texture:</span>
                      <span>{pocketResident.dietaryTexture}</span>
                    </div>
                    <div>
                      <span className="text-slate-600 text-[10px] block font-normal">Fluid Thickness:</span>
                      <span>{pocketResident.fluidConsistency}</span>
                    </div>
                  </div>
                </div>

                {/* Repositioning Schedule */}
                <div className="mt-3 p-2.5 bg-slate-100 border border-slate-300 rounded text-[11px]">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="font-black text-slate-800 block text-[10px] uppercase">Q2H REPOSITION PROTOCOL</span>
                      <span className="text-slate-700">Turn interval: Every 2 hours strictly</span>
                    </div>
                    <div className="text-right">
                      <span className="text-[10px] text-slate-500 block">Current Position:</span>
                      <span className="font-bold font-mono text-slate-900">{pocketResident.turnSide}</span>
                    </div>
                  </div>
                </div>

                {/* Primary Diagnosis & Emergency Contact */}
                <div className="mt-3 pt-3 border-t border-slate-300 text-[10px] space-y-1">
                  <div>
                    <strong className="text-slate-700">Primary Diagnosis: </strong>
                    <span className="text-slate-900">{pocketResident.primaryDiagnosis}</span>
                  </div>
                  <div>
                    <strong className="text-slate-700">Emergency Contact: </strong>
                    <span className="text-slate-900 font-bold">{pocketResident.primaryEmergencyContact.name}</span>{' '}
                    <span>({pocketResident.primaryEmergencyContact.relation}) • </span>
                    <span className="font-mono font-bold text-slate-900">{pocketResident.primaryEmergencyContact.phone}</span>
                  </div>
                </div>

                {/* Badge Cut Lines Footer */}
                <div className="mt-4 pt-3 border-t-2 border-dashed border-slate-400 flex items-center justify-between text-[9px] text-slate-500 font-mono">
                  <span>[ FOLD OR SLIP INTO LANYARD POUCH ]</span>
                  <span>VERIFIED BY CAREGIVER DESK</span>
                </div>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
};
